import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IntervalTypes } from '@guryou/core/build/intervals';
import {
  Block,
  DateRange,
  DateTimePicker,
  DefaultText,
  FlexRow,
  FullScreenSpinner,
  HiddenField,
  MediumText,
  NumberBox,
  SmallGrayText,
} from '@guryou/html-components';
import { saveSession, stateIsLoading, useEffectOnError, useEffectOnSuccess } from '@guryou/redux';
import { addMonths } from '@guryou/utils/build/dateTimeUtils';
import { addMinutes, dateIsBetween, parseDate, printInputDate, toFloatingDisplayDate } from '@guryou/utils/build/dateTimeUtils.datefns';
import SelectableCubeGroup from 'onboard/components/StaffCube';
import WeekBuilder from 'onboard/components/WeekBuilder';
import WorkingDays from 'onboard/components/WorkingDays';
import CheckboxInfoContainer from 'onboard/containers/CheckboxInfoContainer';
import FormContainer from 'onboard/containers/FormContainer';
import InfoContainer from 'onboard/containers/InfoContainer';
import MainContainer from 'onboard/containers/MainContainer';
import PlayGuideVideoEmbed from 'onboard/containers/PlayGuideVideoEmbed';
import TitleContainer from 'onboard/containers/TitleContainer';
import { useOnboardContext } from 'onboard/context';
import { guideVideos } from 'onboard/GuideVideosUrls';
import { dealTokens, SystemCard, SystemCheckbox, SystemFieldError, SystemSelect, SystemTextInput } from 'components/shared';

import StepButtons from '../StepButtons';

const CourseSessions = ({ gyClass }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [repeatDays, setRepeatDays] = useState(false);
  const [repeatIntervalType, setRepeatIntervalType] = useState('weekly');
  const [workingDaysError, setWorkingDaysError] = useState(false);
  const [addedDaysError, setAddedDaysError] = useState(false);
  const [staffError, setStaffError] = useState(false);

  const createSession = useSelector(state => state.sessions.create);

  const { resetCourseStep, serviceType, setActiveStep } = useOnboardContext();

  const [ls, setLs] = useState(() => {
    const initState = {
      startTime: null,
    };
    initState.price = gyClass?.price;
    initState.duration = gyClass?.duration || 5;

    initState.endTime = addMinutes(initState.startTime, initState.duration);
    initState.startDate = gyClass?.startTime || new Date();
    const startPlusMonth = addMonths(gyClass?.startTime || new Date(), 1);
    const maxOneMonth = printInputDate(gyClass?.endTime) > printInputDate(startPlusMonth) ? startPlusMonth : gyClass?.endTime;
    initState.endDate = maxOneMonth || new Date();
    return initState;
  });

  useEffectOnSuccess(
    state => state.sessions.create,
    () => {
      resetCourseStep(3, serviceType);
    }
  );

  useEffectOnError(
    state => state.sessions.create,
    () => {
      setActiveStep(0);
    }
  );

  const handleSubmit = data => {
    if (data.repeatIntervalType === IntervalTypes.weekly && !data.addedDays) {
      setAddedDaysError(true);
      return;
    }

    if (data.repeatIntervalType === IntervalTypes.daily && !data.workingDays) {
      setWorkingDaysError(true);
      return;
    }

    if (!data.resourceId) {
      setStaffError(true);
      return;
    }

    dispatch(saveSession(data, true)); // isDeal = true
  };

  if (stateIsLoading(createSession)) {
    return <FullScreenSpinner />;
  }

  const preselected = gyClass?.resources && gyClass?.resources.length > 0 && [gyClass?.resources[0].id];

  return (
    <MainContainer>
      <FormContainer handleSubmit={handleSubmit}>
        <TitleContainer title={t('ONBOARD.createSessions')} />
        <Block lg={10} md={12} xs={12} margin={0} marginTop={0} flexWrap="wrap">
          <HiddenField name="classId" value={gyClass.id} />
          <HiddenField name="locationType" value="1" />
          <Block margin={0} marginTop={2}>
            <SelectableCubeGroup
              error={staffError}
              setError={setStaffError}
              multiple={false}
              resources={gyClass?.resources || []}
              values={preselected || []}
            />
          </Block>

          <SystemCard style={{ marginTop: dealTokens.space[5] }}>
            <div style={{ marginBottom: dealTokens.space[3] }}>
              <SystemCheckbox checked={repeatDays} onChange={checked => setRepeatDays(checked)} label={t('SESSIONS.repeatSession')} />
            </div>

            {!repeatDays && (
              <FlexRow xs={12} margin={0} padding={0}>
                <DateTimePicker
                  xs={12}
                  shouldDisableDate={date => !dateIsBetween(date, parseDate(gyClass.startTime), parseDate(gyClass.endTime))}
                  margin="10px 0px 20px 0px"
                  label={t('GENERAL.from')}
                  dateFieldName="startDate"
                  value={ls.startDate}
                  disablePast={false}
                />
              </FlexRow>
            )}
            {repeatDays && (
              <>
                <FlexRow xs={12} margin={0} padding={0} justifyContent="space-between">
                  <Block xs={5.8} display="flex" margin="10px 0px 20px 0px" alignItems="end" justifyContent="space-between">
                    <SmallGrayText xs={5} textAlign="left">
                      {t('SESSIONS.repeatEvery')}
                    </SmallGrayText>
                    <SystemTextInput
                      type="number"
                      disabled={printInputDate(ls.startDate) === printInputDate(ls.endDate)}
                      name="repeatInterval"
                      defaultValue="1"
                    />
                  </Block>
                  <SystemSelect
                    required
                    none={false}
                    name="repeatIntervalType"
                    value={repeatIntervalType}
                    options={[
                      { value: IntervalTypes.weekly, label: t('SESSIONS.weeks') },
                      { value: IntervalTypes.daily, label: t('SESSIONS.days') },
                    ]}
                    onChange={val => setRepeatIntervalType(val)}
                  />
                </FlexRow>
                <FlexRow xs={12} margin={0} padding={0} justifyContent="space-between">
                  <DateRange
                    inline={true}
                    xs={5.8}
                    wrapperMargin={0}
                    wrapperJustifyContent="space-between"
                    margin="10px 0px 20px 0px"
                    startDateFieldName="startDate"
                    endDateFieldName="endDate"
                    startValue={ls.startDate || new Date()}
                    endValue={ls.endDate || addMonths(new Date(), 1)}
                    startWithNullDate={true}
                    min={gyClass?.startTime}
                    max={gyClass?.endTime}
                    shouldDisableDate={date => !dateIsBetween(date, gyClass?.startTime, gyClass?.endTime)}
                    disablePast={false}
                  />
                </FlexRow>
              </>
            )}

            {/* Shared regardless of repeatDays — same DateTimePicker instances persist across the
                checkbox toggle instead of unmounting/remounting. Duplicating this block per branch
                (as it originally was) meant toggling the checkbox swapped which JSX subtree rendered,
                forcing a fresh mount of the "same" startTime field — DateTimePicker.js's useState
                initializer re-parses its `value` through `parseDate`, which force-appends a 'Z' onto
                a plain string and reinterprets it as UTC, shifting an already-correct picked time by
                the local UTC offset. `toFloatingDisplayDate` doesn't help here since it only
                compensates for real `Date` objects, not this remount-reparse-of-a-string case — the
                actual fix is to stop remounting the field at all. */}
            <FlexRow xs={12} margin={0} padding={0}>
              <NumberBox
                xs={12}
                fullWidth
                margin="10px 0px 20px 0px"
                name="duration"
                value={ls.duration}
                label={t('ONBOARD.duration')}
                required
                disabled
                onChange={val => {
                  setLs(s => ({ ...s, duration: val, endTime: addMinutes(s.startTime, val) }));
                }}
              />
            </FlexRow>
            <FlexRow xs={12} margin={0} padding={0} justifyContent="space-between">
              <DateTimePicker
                xs={5.8}
                margin="10px 0px 20px 0px"
                label={t('GENERAL.from')}
                timeFieldName="startTime"
                value={ls.startTime}
                onChange={val => {
                  setLs(s => ({ ...s, startTime: val, endTime: addMinutes(val, s.duration) }));
                }}
              />
              <DateTimePicker
                xs={5.8}
                margin="10px 0px 20px 0px"
                key={+ls.endTime}
                label={t('GENERAL.to')}
                timeFieldName="endTime"
                value={toFloatingDisplayDate(ls.endTime)}
                disableField={true}
              />
            </FlexRow>
          </SystemCard>

          {repeatDays && repeatIntervalType === 'daily' && (
            <SystemCard style={{ marginTop: dealTokens.space[4] }}>
              <WorkingDays />
              {workingDaysError && <SystemFieldError>{`* ${t('SESSIONS.selectDay')}`}</SystemFieldError>}
            </SystemCard>
          )}

          {repeatDays && repeatIntervalType === 'weekly' && (
            <SystemCard style={{ marginTop: dealTokens.space[4] }}>
              <WeekBuilder gyClass={gyClass} ls={ls} />
              {addedDaysError && <SystemFieldError>{`* ${t('SESSIONS.selectDay')}`}</SystemFieldError>}
            </SystemCard>
          )}

          <HiddenField value={ls.duration} name="duration"></HiddenField>
        </Block>
        <StepButtons finish={true} showPreviousButton={false} />
      </FormContainer>
      <InfoContainer>
        <MediumText lineHeight="1.3">{t('ONBOARD_TEXT.courseSessionsTitle')}</MediumText>
        <DefaultText color={dealTokens.color.textMuted} marginTop={2}>
          {t('ONBOARD_TEXT.courseSessionsSubtitle')}
        </DefaultText>

        <div style={{ marginTop: dealTokens.space[6], paddingTop: dealTokens.space[5], borderTop: `1px solid ${dealTokens.color.border}` }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: dealTokens.font.weightSemibold,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: dealTokens.color.textMuted,
              marginBottom: dealTokens.space[4],
            }}
          >
            {t('ONBOARD_TEXT.courseSessionsCheckboxTitle')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: dealTokens.space[4] }}>
            <CheckboxInfoContainer text={t('ONBOARD_TEXT.courseSessionsCheckbox1Text')} />
            <CheckboxInfoContainer text={t('ONBOARD_TEXT.courseSessionsCheckbox2Text')} />
            <CheckboxInfoContainer text={t('ONBOARD_TEXT.courseSessionsCheckbox3Text')} />
          </div>
        </div>

        <PlayGuideVideoEmbed videoId={guideVideos[`${serviceType}Sessions`].videoId} modalTitle={guideVideos[`${serviceType}Sessions`].title} />
      </InfoContainer>
    </MainContainer>
  );
};

export default CourseSessions;
